import {useGetNextTrainingQuery} from '@api';
import React, {useEffect, useState} from 'react';
import {Spinner} from '@goproblems/ui';
import {isFetchBaseQueryError} from '@utils/fetch';
import {useTranslation} from 'react-i18next';
import {useHeaderNavigation} from '@components/hooks/useHeaderNavigation';

/**
 * Convert ISO 8601 duration (e.g. "P0Y0M30DT23H50M8S") to total seconds.
 */
function parseISODuration(duration: string | null | undefined): number {
    if (!duration) return 0;
    const match =
        /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/.exec(
            duration
        );
    if (!match) return 0;
    const [, years, months, days, hours, minutes, seconds] = match.map(Number);
    return (
        (years || 0) * 365 * 24 * 3600 +
        (months || 0) * 30 * 24 * 3600 +
        (days || 0) * 24 * 3600 +
        (hours || 0) * 3600 +
        (minutes || 0) * 60 +
        (seconds || 0)
    );
}

/**
 * Format seconds into "DD:HH:MM:SS"
 */
function formatTime(totalSeconds: number): string {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days > 0 ? `${days}d ` : ''}${hours
        .toString()
        .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
}

type CountdownTimerProps = {
    /** ISO 8601 duration string */
    timeUntilNextReady: string | null | undefined;
    onReady?: () => void;
    interactive?: boolean;
};

const CountdownTimer: React.FC<CountdownTimerProps> = ({
    timeUntilNextReady,
    onReady,
    interactive = true,
}) => {
    const [secondsLeft, setSecondsLeft] = useState(
        parseISODuration(timeUntilNextReady)
    );
    const {handleTrainingClick} = useHeaderNavigation();
    const {t} = useTranslation();

    useEffect(() => {
        if (secondsLeft === 0) {
            onReady?.();
        }
    }, [onReady, secondsLeft]);

    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsLeft(prev => Math.max(prev - 1, 0));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    if (secondsLeft <= 0) {
        if (!interactive) {
            return <span>{t('training.ready_now')}</span>;
        }

        return (
            <span
                className="cursor-pointer underline"
                onClick={handleTrainingClick}>
                {t('training.ready_now')}
            </span>
        );
    }

    return (
        <span aria-disabled={true}>
            {t('training.next_in', {time: formatTime(secondsLeft)})}
        </span>
    );
};

function TrainingCountdown(props: {onReady?: () => void; interactive?: boolean}) {
    const {t} = useTranslation();
    const {data, isLoading, error, isError} = useGetNextTrainingQuery();
    const nextTrainingIn = data?.timeUntilNextReady || null;
    const [isNoNext, setIsNoNext] = useState(false);

    useEffect(() => {
        if (isError && isFetchBaseQueryError(error) && error.status === 404) {
            console.info('No problems:', error);
            setIsNoNext(true);
        }
    }, [isError, error]);

    return (
        <>
            {isLoading ? (
                <>
                    {' '}
                    <Spinner size={'sm'} />
                </>
            ) : error ? (
                isNoNext ? (
                    <> {t('training.no_more_available')} </>
                ) : (
                    <> {t('training.error_loading_next_time')} </>
                )
            ) : (
                <CountdownTimer
                    timeUntilNextReady={nextTrainingIn}
                    onReady={props.onReady}
                    interactive={props.interactive}
                />
            )}
        </>
    );
}

export default TrainingCountdown;
